/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import {
  Auth0Client as Auth0LibClient,
  Auth0ClientOptions,
  IdToken,
  LogoutOptions,
  TokenEndpointResponse,
} from '@auth0/auth0-spa-js';
import type {
  AdaptableAuthClient,
  AuthClientLoader,
  AuthWithRedirectParams,
} from '../adapter';
import { removeAllTokens } from '../utils/cache';
import { decodeJwt } from '../utils/jwt';

export class Auth0ClientAdapter
  extends Auth0LibClient
  implements AdaptableAuthClient
{
  private _auth0Config: Auth0ClientOptions;

  constructor(options: Auth0ClientOptions) {
    super(options);
    this._auth0Config = options;
  }

  async signUp({
    language = 'en',
    search,
    appState = {},
    authPageParams = {},
    redirectUrl,
  }: AuthWithRedirectParams) {
    await this.loginWithRedirect({
      initialScreen: 'signUp',
      lang: language,
      redirect_uri:
        typeof redirectUrl !== 'undefined'
          ? redirectUrl
          : this._createRedirectUri(search),
      appState,
      ...authPageParams,
    });
  }

  async login({
    language = 'en',
    search,
    appState = {},
    authPageParams = {},
    redirectUrl,
  }: AuthWithRedirectParams) {
    await this.loginWithRedirect({
      initialScreen: 'login',
      lang: language,
      redirect_uri:
        typeof redirectUrl !== 'undefined'
          ? redirectUrl
          : this._createRedirectUri(search),
      appState,
      ...authPageParams,
    });
  }

  async forgotPassword({
    language = 'en',
    search,
    appState = {},
    authPageParams = {},
    redirectUrl,
  }: AuthWithRedirectParams) {
    await this.loginWithRedirect({
      initialScreen: 'forgotPassword',
      lang: language,
      redirect_uri:
        typeof redirectUrl !== 'undefined'
          ? redirectUrl
          : this._createRedirectUri(search),
      appState,
      ...authPageParams,
    });
  }

  async logoutSession(options: LogoutOptions = {}) {
    this.logout({
      returnTo: window.location.origin,
      ...options,
    });
  }

  async storeTokens(
    authResult: TokenEndpointResponse
  ): Promise<IdToken | undefined> {
    const decodedToken = decodeJwt(authResult.id_token);

    const cacheEntry = {
      ...authResult,
      decodedToken,
      scope: this._auth0Config.scope,
      audience: this._auth0Config.audience || 'default',
      client_id: this._auth0Config.client_id,
    };

    /* eslint-disable @typescript-eslint/ban-ts-comment */
    // @ts-ignore
    await this.cacheManager.set(cacheEntry);

    // @ts-ignore
    this.cookieStorage.save(this.isAuthenticatedCookieName, true, {
      daysUntilExpire: this._auth0Config.sessionCheckExpiryDays,
      cookieDomain: this._auth0Config.cookieDomain,
    });

    // @ts-ignore
    this._processOrgIdHint(decodedToken.claims.org_id);
    /* eslint-enable @typescript-eslint/ban-ts-comment */

    return decodedToken.claims;
  }

  clearLocalCache() {
    removeAllTokens(this._auth0Config.client_id);
  }

  private _createRedirectUri(search: AuthWithRedirectParams['search']): string {
    if (search === undefined) {
      return this._auth0Config.redirect_uri as string;
    }

    if (search[0] === '?') {
      search = search.slice(1);
    }

    return `${this._auth0Config.redirect_uri}?${search}`;
  }
}

let _auth0ClientAdapter: Auth0ClientAdapter | undefined;

const auth0ClientAdapter = (
  options: Auth0ClientOptions
): Auth0ClientAdapter => {
  if (!_auth0ClientAdapter) {
    _auth0ClientAdapter = new Auth0ClientAdapter(options);
  }

  return _auth0ClientAdapter;
};

export const auth0ClientAdapterLoader =
  (options: Auth0ClientOptions): AuthClientLoader<Auth0ClientAdapter> =>
  () => {
    return auth0ClientAdapter(options);
  };
